import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { Subject, debounceTime } from 'rxjs';

import { CatalogObjectTypes, SubMenuOption } from '@mirage-projects/cw-models';

import { GridColumn } from '../../models/grid-column';
import { PaginationData } from '../../models/pagination-data';
import { ResolvePropertyPipe } from '../../pipes/resolve-property/resolve-property.pipe';
import { GridService } from './grid.service';

export interface GridCheckedAllChange {
    isChecked: boolean;
    column: GridColumn;
}
export interface GridRowData {
    id: number;
    row: any;
}

export type IconActionRow = {
    action: string;
    row: any;
    idx: number;
};

/** This Grid component is an generic grid that allows (multi)selection, so the user can select one or more rows. It is paginated, so we don't have to load all the information once.*/
@Component({
    selector: 'cw-grid',
    templateUrl: './grid.component.html',
    styleUrls: ['./grid.component.scss'],
    providers: [ResolvePropertyPipe],
    standalone: false,
})
export class GridComponent implements OnInit {
    /** Data to be displayed in the body of the table. Each array element is a row and will contain one property for each column. For example: {[col1: 'aaa', col2:'bbb'],[col1: 'ccc', col2:'ddd']}*/
    @Input() set data(value: any[]) {
        this.pagedData = value;
        const hasUncheckedElements = this.pagedData.some((item) => !item.selected);
        this.updateSelectAllCheckbox(hasUncheckedElements);
    }
    get data(): any[] {
        return this.pagedData;
    }
    /** The data of the columns you show on your table The columns are set programatically. */
    @Input() columns: GridColumn[] | undefined;
    /** Column name that acts as id. It will be used to select rows, for example */
    @Input() columnId?: string;
    /** To Enabled/disabled selecting rows */
    @Input() isEnabledSelectRow = false;
    /** Selected row by default in grid */
    @Input() selectedRow: any;
    /** The data of the paginator like current page size page elements to show */
    @Input() pagination: PaginationData | undefined;
    /** Type of Catalog object */
    @Input() catalogObjectType: CatalogObjectTypes = 'projects';
    /** Multiselection mode */
    @Input() isMultiselection = false;
    /** Shows grid with border and shadow */
    @Input() showBorder = false;

    /** Output to catch the page change event */
    @Output() currentPageChange = new EventEmitter<number>();
    /** Output to catch the selection of a row */
    @Output() selectedRowChange = new EventEmitter<any>();
    /** Output to catch the dblClick event over a row */
    @Output() dblClickRow = new EventEmitter<any>();
    /** Output to catch when a row is checked */
    @Output() elementCheck = new EventEmitter<any>();
    /** Event emitted when the user clicks on the close icon */
    @Output() secondaryMenuClick = new EventEmitter<{ menuOption: SubMenuOption; row: any }>();
    /** Event emitted when the user clicks on a icon action */
    @Output() iconAction = new EventEmitter<IconActionRow>();
    /** Event emitted when the user clicks on a link field */
    @Output() linkFieldClick = new EventEmitter<GridRowData>();
    /** Event emitted when the user clicks on a select all checkbox column */
    @Output() checkedAllChange = new EventEmitter<GridCheckedAllChange>();

    currentPage = 1;

    isCheckAllChecked = false;

    private pagedData: any[] = [];
    private changePageSubject: Subject<number> = new Subject<number>();

    constructor(
        private gridService: GridService,
        private resolvePropertyPipe: ResolvePropertyPipe
    ) {}

    ngOnInit(): void {
        this.initChangePageSubscription();

        if (this.pagination) {
            this.currentPage = this.pagination.currentPage;
        }
    }

    /**
     *This method is triggered when a secondary menu option is clicked.
     * It emits an event with the selected menu option and the corresponding row.
     * @param menuOption
     * @param row
     */
    onSecondaryMenuClicked(menuOption: SubMenuOption, row: any): void {
        this.secondaryMenuClick.emit({ menuOption, row });
    }

    /**
     *This method is used to change the current page to a new page.
     * It checks if the new page is different from the current page, and if so, it updates the current page and emits an event with the new page number.
     * @param newPage
     */
    onChangePage(newPage: number): void {
        if (this.currentPage !== newPage) {
            this.currentPage = newPage;
            this.changePageSubject.next(newPage);
        }
    }

    /**
     * Method to check or uncheck all checkbox of the elements on the grid
     */
    onCheckAllChange(value: boolean, column: GridColumn): void {
        this.isCheckAllChecked = value;
        this.data.forEach((item) => {
            const isLayerIncomplete = this.catalogObjectType === 'layers' && item.catalogObject?.incomplete;

            if (!isLayerIncomplete) {
                item.selected = this.isCheckAllChecked;
                this.gridService.updateCheckedElementsState(item.catalogObject, this.isCheckAllChecked, this.catalogObjectType);
            }
        });

        this.checkedAllChange.emit({ isChecked: value, column });
    }

    /**
     * This method is triggered when a row is selected.
     * If the selected row is not already selected, it updates the selected row and emits an event with the new selected row.
     * @param row
     */
    onSelectRow(row: any): void {
        if (!this.isSelectedRow(row) && this.isEnabledSelectRow) {
            this.selectedRow = row;
            this.selectedRowChange.emit(this.selectedRow);
        }
    }

    /**
     * Emits the dblClickRow event
     * @param row
     */
    onDblClickRow(row: any): void {
        this.dblClickRow.emit(row);
    }

    /**
     * This method stops the propagation of an event up the DOM tree, preventing any parent handlers from being notified of the event.
     * @param event
     */
    preventEventPropagation(event: Event): void {
        event.stopPropagation();
    }

    /**
     * Method to handle the logic of checking an row in the grid
     * @param row
     */
    onElementChecked(row: any): void {
        this.isCheckAllChecked = this.data.every((item) => item.selected);
        this.elementCheck.emit(row);
    }

    /**
     * This method checks if a row is the currently selected row. It returns true if the row is selected, and false otherwise.
     * @param row
     * @returns
     */
    isSelectedRow(row: any): boolean {
        if (!this.columnId || !row || !this.selectedRow) {
            return false;
        }
        return row[this.columnId] === this.selectedRow[this.columnId];
    }

    /**
     * This method checks if a column is visible.
     * It returns true if the column is visible, and false otherwise.
     * @param column
     * @returns
     */
    isVisible(column: GridColumn, row?: any): boolean {
        const isCellVisible = row ? (column.isCellVisible?.(column, row) ?? true) : true;

        return column.isColumnVisible !== false && isCellVisible;
    }

    emitIconAction(action: string, row: any, idx: number, event: Event): void {
        this.iconAction.emit({ action, row, idx });
        this.preventEventPropagation(event);
    }

    /**
     * emits the path of the field selected
     * @param field the field selected within a row
     * @param event Event
     */
    emitLinkClick(row: any, field: string, event: Event): void {
        const id = this.resolvePropertyPipe.transform(row, field);
        const linkFieldData = { id: id, row: row.catalogObject };
        this.linkFieldClick.emit(linkFieldData);
        this.preventEventPropagation(event);
    }

    /**
     * Creates a unique id string for the checkbox
     * @param index row index
     * @param id column id
     */
    getCheckboxId(index: number, id: any): string {
        return `${index.toString()}-${id}`;
    }

    /**
     * Returns true if the column has to show a multiselection checkbox
     * @param columnItem current column
     */
    hasToShowMultiselectionCheckbox(columnItem: GridColumn): boolean {
        const columnIsMultiselection = columnItem.multiSelection ?? false;
        const columnIsCheckboxType = columnItem.type === 'checkbox';

        return columnIsCheckboxType && columnIsMultiselection && this.isMultiselection;
    }

    /**
     * Returns true if the row has an icon defned
     * @param row
     * @returns
     */
    hasIcon(row: any, column: GridColumn): boolean {
        return !!row.catalogObject?.icon || !!row?.[column.field]?.icon;
    }

    private initChangePageSubscription(): void {
        /*
        Right now TWC emits the event twice.
        This debounce time is to fix this. Remove it when TWC corrects this issue.
        */
        this.changePageSubject.pipe(debounceTime(50)).subscribe((newPage) => {
            if (!this.pagination) {
                return;
            }
            this.pagination.currentPage = newPage;
            this.currentPageChange.emit(newPage);
        });
    }

    get showPagination(): boolean {
        if (!this.pagination) {
            return false;
        }
        return this.pagination.totalElements > this.pagination.pageSize;
    }

    private updateSelectAllCheckbox(hasUncheckedElements: boolean): void {
        setTimeout(() => {
            this.isCheckAllChecked = !hasUncheckedElements;
        });
    }
}
